import { useState, useEffect, useMemo } from 'react'
import { getTrackerPrinters } from '../services/api'
import { statusClasses } from './trackerFields'
import { Spinner } from './trackerShared'

// Printers view — print-queue + fleet, from sheet col E (Printer) × col F (Status).
// Printing / Queued jobs sit on their printer card; Processing (no printer yet)
// is "Preparing". A flat searchable list sits below. Read-only, like the board.

function JobLine({ j, accent }) {
  return (
    <div className="flex items-center gap-2 text-xs py-1">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${accent}`} />
      <span className="font-mono text-gray-600 truncate" title={j.stlName}>{j.stlName || '—'}</span>
      <span className="text-gray-400 shrink-0">{j.caseNo || ''}</span>
    </div>
  )
}

function PrinterCard({ p }) {
  const busy = p.printing.length > 0
  return (
    <div className={`rounded-xl border p-3.5 ${busy ? 'border-brand-200 bg-brand-50/40' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-semibold text-gray-800 text-sm">{p.label}</div>
          <div className="text-[11px] text-gray-400">{p.type}</div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${busy ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-400'}`}>
          {busy ? 'Printing' : 'Idle'}
        </span>
      </div>
      {p.printing.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Printing now</div>
          {p.printing.map((j) => <JobLine key={(j.caseNo || '') + '-' + j.seq} j={j} accent="bg-brand-500" />)}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Queue ({p.queued.length})</div>
        {p.queued.length === 0
          ? <div className="text-xs text-gray-400 italic">empty</div>
          : p.queued.map((j) => <JobLine key={(j.caseNo || '') + '-' + j.seq} j={j} accent="bg-amber-400" />)}
      </div>
    </div>
  )
}

export default function TrackerPrinters() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')

  useEffect(() => {
    let alive = true
    setLoading(true); setError('')
    getTrackerPrinters()
      .then((r) => { if (alive) setData(r.data) })
      .catch((e) => { if (alive) setError(e.response?.data?.error || 'Failed to load printers.') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const list = useMemo(() => {
    const rows = data?.list || []
    const s = q.trim().toLowerCase()
    if (!s) return rows
    return rows.filter((j) => [j.stlName, j.caseNo, j.printer, j.status].some((v) => (v || '').toLowerCase().includes(s)))
  }, [data, q])

  if (loading) return <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center"><Spinner className="h-4 w-4" /> Loading printers…</div>
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>

  const c = data.counts
  return (
    <div>
      <div className="flex flex-wrap gap-2.5 mb-4">
        <Stat label="Printing" value={c.printing} tone="brand" />
        <Stat label="Queued" value={c.queued} tone="amber" />
        <Stat label="Preparing" value={c.processing} tone="default" />
      </div>

      {/* fleet — printer perspective */}
      <div className="grid gap-2.5 mb-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
        {data.fleet.map((p) => <PrinterCard key={p.id} p={p} />)}
      </div>

      {/* preparing — not yet assigned to a machine */}
      {data.preparing.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Preparing <span className="text-gray-400 font-normal">· not yet on a printer ({data.preparing.length})</span></h3>
          <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
            {data.preparing.map((j) => (
              <div key={j.seq + (j.caseNo || '')} className="flex items-center gap-2 px-3.5 py-2 text-sm">
                <span className="font-mono text-xs text-gray-600 truncate flex-1" title={j.stlName}>{j.stlName || '—'}</span>
                <span className="text-gray-400 text-xs truncate max-w-[160px]">{j.caseNo}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusClasses(j.status)}`}>{j.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* all print jobs list */}
      <h3 className="text-sm font-semibold text-gray-700 mb-2">All print jobs <span className="text-gray-400 font-normal">({list.length})</span></h3>
      <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search STL, case, printer…" className="input w-full text-sm mb-3" />
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-100">
            <th className="px-3 py-2 font-medium">STL</th>
            <th className="px-3 py-2 font-medium">Case</th>
            <th className="px-3 py-2 font-medium">Printer</th>
            <th className="px-3 py-2 font-medium text-right">Status</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {list.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">No print jobs.</td></tr>}
            {list.map((j) => (
              <tr key={j.seq + (j.caseNo || '')} className="text-gray-700">
                <td className="px-3 py-2 font-mono text-xs text-gray-600 truncate max-w-[320px]" title={j.stlName}>{j.stlName || '—'}</td>
                <td className="px-3 py-2 text-gray-500 truncate max-w-[200px]">{j.caseNo || ''}</td>
                <td className="px-3 py-2 text-gray-500">{j.printer || <span className="text-gray-300">—</span>}</td>
                <td className="px-3 py-2 text-right"><span className={`text-xs px-2 py-0.5 rounded-full ${statusClasses(j.status)}`}>{j.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const TONES = { brand: 'bg-brand-50 border-brand-200 text-brand-700', amber: 'bg-amber-50 border-amber-200 text-amber-700', default: 'bg-white border-gray-200 text-gray-800' }
function Stat({ label, value, tone }) {
  return (
    <div className={`rounded-lg border px-3.5 py-2 min-w-[96px] ${TONES[tone] || TONES.default}`}>
      <div className="text-xl font-semibold leading-none tabular-nums">{value ?? 0}</div>
      <div className="text-xs mt-1 opacity-80">{label}</div>
    </div>
  )
}
